import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Search, Sparkles, Zap, Target, DollarSign, TrendingUp, Filter, ArrowUpDown,
  X, Copy, Send, RefreshCw, ExternalLink, Star, Globe, Phone, MapPin, CheckCircle2,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const scoreColor = (s) => {
  if (s >= 85) return { bg: "#10B981", text: "text-emerald-400", ring: "border-emerald-400/40", chip: "bg-emerald-400/10 text-emerald-300" };
  if (s >= 60) return { bg: "#F59E0B", text: "text-amber-400", ring: "border-amber-400/40", chip: "bg-amber-400/10 text-amber-300" };
  return { bg: "#F43F5E", text: "text-rose-400", ring: "border-rose-400/40", chip: "bg-rose-400/10 text-rose-300" };
};

const statusColors = {
  new: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  contacted: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  qualified: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  won: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  archived: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};

const fmtCurrency = (n) => "$" + (n || 0).toLocaleString();

export default function ScoutPulse() {
  const [meta, setMeta] = useState(null);
  const [location, setLocation] = useState("Memphis, TN");
  const [category, setCategory] = useState("Dentists");
  const [minScore, setMinScore] = useState(0);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, high_potential: 0, pipeline_value: 0, avg_score: 0 });
  const [searching, setSearching] = useState(false);
  const [source, setSource] = useState(null); // 'live' | 'sample'
  const [sortBy, setSortBy] = useState("score");
  const [filterText, setFilterText] = useState("");
  const [selectedLead, setSelectedLead] = useState(null);
  const [pitchLead, setPitchLead] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [m, l, s] = await Promise.all([
          api.get("/scout/meta"),
          api.get("/scout/leads"),
          api.get("/scout/stats"),
        ]);
        setMeta(m.data);
        setLeads(l.data);
        setStats(s.data);
        if (m.data?.categories?.length && !m.data.categories.includes(category)) {
          setCategory(m.data.categories[0]);
        }
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load ScoutPulse");
      }
    })();
  }, []);

  const refreshStats = async () => {
    try {
      const { data } = await api.get("/scout/stats");
      setStats(data);
    } catch { /* noop */ }
  };

  const runSearch = async () => {
    if (!location.trim() || !category.trim()) {
      toast.error("Location and category are required.");
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.post("/scout/search", {
        location: location.trim(),
        category,
        min_score: Number(minScore) || 0,
        limit: 20,
      });
      setLeads(data.leads);
      setSource(data.source);
      await refreshStats();
      toast.success(`${data.count} leads scouted from ${data.source === "live" ? "Google Places" : "sample data"}.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Scout failed");
    } finally {
      setSearching(false);
    }
  };

  const updateStatus = async (lead, status) => {
    try {
      const { data } = await api.patch(`/scout/leads/${lead.id}/status`, { status });
      setLeads((arr) => arr.map((l) => (l.id === lead.id ? data : l)));
      if (selectedLead?.id === lead.id) setSelectedLead(data);
      toast.success(`Status → ${status}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const filtered = useMemo(() => {
    let arr = [...leads];
    if (filterText.trim()) {
      const q = filterText.trim().toLowerCase();
      arr = arr.filter((l) =>
        (l.name || "").toLowerCase().includes(q) ||
        (l.address || "").toLowerCase().includes(q) ||
        (l.recommended_service || "").toLowerCase().includes(q)
      );
    }
    if (sortBy === "score") arr.sort((a, b) => b.score - a.score);
    if (sortBy === "rating") arr.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    if (sortBy === "reviews") arr.sort((a, b) => (b.review_count || 0) - (a.review_count || 0));
    if (sortBy === "value") arr.sort((a, b) => (b.estimated_value || 0) - (a.estimated_value || 0));
    return arr;
  }, [leads, filterText, sortBy]);

  return (
    <div className="text-slate-100 font-body" data-testid="scoutpulse-root">
      {/* Ambient grid + glow */}
      <div className="relative rounded-lg overflow-hidden border border-slate-800/70" style={{ background: "#0B0F17" }}>
        <div className="absolute inset-0 pointer-events-none opacity-40"
             style={{
               backgroundImage: "linear-gradient(rgba(99,102,241,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,0.08) 1px, transparent 1px)",
               backgroundSize: "48px 48px",
             }} />
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full pointer-events-none"
             style={{ background: "radial-gradient(closest-side, rgba(99,102,241,0.22), transparent 70%)" }} />

        {/* STICKY HEADER */}
        <div className="relative border-b border-slate-800/70 backdrop-blur bg-slate-950/50 px-6 py-5 flex items-center justify-between flex-wrap gap-4"
             style={{ position: "sticky", top: 0, zIndex: 30 }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded flex items-center justify-center border border-indigo-500/40 bg-indigo-500/10">
              <Sparkles size={18} className="text-indigo-300" />
            </div>
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight text-white" style={{ fontFamily: "Outfit, Inter, sans-serif" }}>
                ScoutPulse AI
              </h2>
              <p className="text-xs text-slate-400 font-mono tracking-wider" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                LEAD-GEN COMMAND CENTER
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              label={meta?.live_data ? "Live · Google Places" : "Sample Data"}
              tone={meta?.live_data ? "emerald" : "amber"}
              testid="badge-source"
            />
            <Badge label={meta?.model || "Gemini 3.1 Pro"} tone="indigo" testid="badge-model" />
          </div>
        </div>

        {/* SEARCH BAR */}
        <div className="relative px-6 py-6 border-b border-slate-800/60">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-4">
              <Label>Location</Label>
              <input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Memphis, TN"
                data-testid="scout-location"
                className="w-full bg-slate-900/60 border border-slate-700 hover:border-indigo-500/60 focus:border-indigo-500 outline-none rounded px-3 py-2.5 text-sm text-slate-100 transition-colors"
              />
            </div>
            <div className="md:col-span-3">
              <Label>Category</Label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="scout-category"
                className="w-full bg-slate-900/60 border border-slate-700 focus:border-indigo-500 outline-none rounded px-3 py-2.5 text-sm text-slate-100"
              >
                {(meta?.categories || []).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-3">
              <Label>Min score: <span className="text-indigo-300">{minScore}</span></Label>
              <input
                type="range" min={0} max={100} step={5}
                value={minScore} onChange={(e) => setMinScore(e.target.value)}
                data-testid="scout-min-score"
                className="w-full accent-indigo-500 mt-2"
              />
            </div>
            <div className="md:col-span-2 flex items-end">
              <button
                onClick={runSearch}
                disabled={searching}
                data-testid="scout-run"
                className="w-full inline-flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 text-white px-4 py-2.5 rounded font-mono text-xs uppercase tracking-widest transition-colors"
                style={{ fontFamily: "JetBrains Mono, monospace" }}
              >
                {searching ? <RefreshCw size={14} className="animate-spin" /> : <Search size={14} />}
                {searching ? "Scouting..." : "Scout Leads"}
              </button>
            </div>
          </div>
        </div>

        {/* KPI CARDS */}
        <div className="relative px-6 py-6 grid grid-cols-2 lg:grid-cols-4 gap-4 border-b border-slate-800/60">
          <Kpi icon={Target} label="Total Leads" value={stats.total} tone="indigo" delay={0} testid="kpi-total" />
          <Kpi icon={Zap} label="High-Potential (>80)" value={stats.high_potential} tone="emerald" delay={0.06} testid="kpi-high" />
          <Kpi icon={DollarSign} label="Est. Pipeline Value" value={fmtCurrency(stats.pipeline_value)} tone="amber" delay={0.12} testid="kpi-pipeline" />
          <Kpi icon={TrendingUp} label="Avg Score" value={stats.avg_score} tone="rose" delay={0.18} testid="kpi-avg" />
        </div>

        {/* TABLE CONTROLS */}
        <div className="relative px-6 py-4 flex items-center gap-3 flex-wrap border-b border-slate-800/60">
          <div className="flex items-center gap-2 bg-slate-900/60 border border-slate-700 rounded px-2.5 py-1.5 flex-1 min-w-[200px]">
            <Filter size={14} className="text-slate-400" />
            <input
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder="Filter by name, address, service..."
              data-testid="scout-filter"
              className="bg-transparent outline-none text-sm text-slate-100 w-full"
            />
          </div>
          <div className="flex items-center gap-2 bg-slate-900/60 border border-slate-700 rounded px-2.5 py-1.5">
            <ArrowUpDown size={14} className="text-slate-400" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              data-testid="scout-sort"
              className="bg-transparent outline-none text-sm text-slate-100"
            >
              <option value="score">Sort: Score</option>
              <option value="rating">Sort: Rating</option>
              <option value="reviews">Sort: Reviews</option>
              <option value="value">Sort: Est. Value</option>
            </select>
          </div>
          <div className="text-xs text-slate-400 font-mono" style={{ fontFamily: "JetBrains Mono, monospace" }}>
            {filtered.length} / {leads.length} leads {source && `· ${source.toUpperCase()}`}
          </div>
        </div>

        {/* LEADS TABLE */}
        <div className="relative overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-800/70"
                  style={{ fontFamily: "JetBrains Mono, monospace" }}>
                <th className="px-6 py-3 w-16">Rank</th>
                <th className="px-4 py-3">Business</th>
                <th className="px-4 py-3">Rating</th>
                <th className="px-4 py-3">Web</th>
                <th className="px-4 py-3">Matched Service</th>
                <th className="px-4 py-3">Value</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence>
                {filtered.map((l, idx) => {
                  const sc = scoreColor(l.score);
                  return (
                    <motion.tr
                      key={l.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.25, delay: Math.min(idx * 0.02, 0.4) }}
                      className="border-b border-slate-800/50 hover:bg-slate-900/40 transition-colors"
                      data-testid={`lead-row-${l.id}`}
                    >
                      <td className="px-6 py-4">
                        <div className={`w-11 h-11 rounded-full border ${sc.ring} flex items-center justify-center font-bold text-sm`}
                             style={{ color: sc.bg, boxShadow: `0 0 20px ${sc.bg}22 inset` }}>
                          {l.score}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="font-medium text-slate-100">{l.name}</div>
                        <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                          <MapPin size={11} /> {l.address || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {l.rating ? (
                          <div className="flex items-center gap-1 text-amber-300">
                            <Star size={13} fill="currentColor" />
                            <span className="font-medium">{l.rating}</span>
                            <span className="text-slate-500 text-xs ml-1">({l.review_count})</span>
                          </div>
                        ) : <span className="text-slate-500">—</span>}
                      </td>
                      <td className="px-4 py-4">
                        {l.website ? (
                          <a href={l.website} target="_blank" rel="noreferrer"
                             className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200 text-xs">
                            <Globe size={11} /> live
                          </a>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-rose-300 text-xs">
                            <X size={11} /> none
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-xs text-indigo-300">{l.recommended_service}</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="font-mono text-sm text-slate-200" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                          {fmtCurrency(l.estimated_value)}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <select
                          value={l.status || "new"}
                          onChange={(e) => updateStatus(l, e.target.value)}
                          data-testid={`lead-status-${l.id}`}
                          className={`text-xs border rounded px-2 py-1 bg-transparent ${statusColors[l.status || "new"]}`}
                        >
                          {(meta?.statuses || ["new", "contacted", "qualified", "won", "archived"]).map((s) => (
                            <option key={s} value={s} className="bg-slate-900">{s}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="inline-flex gap-2">
                          <button
                            onClick={() => setSelectedLead(l)}
                            data-testid={`lead-score-${l.id}`}
                            className="text-xs px-2.5 py-1 border border-slate-700 hover:border-indigo-500 hover:text-indigo-300 rounded transition-colors"
                          >
                            Score
                          </button>
                          <button
                            onClick={() => setPitchLead(l)}
                            data-testid={`lead-pitch-${l.id}`}
                            className="text-xs px-2.5 py-1 bg-indigo-500 hover:bg-indigo-400 text-white rounded transition-colors inline-flex items-center gap-1"
                          >
                            <Send size={11} /> Pitch
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </AnimatePresence>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center text-slate-500 text-sm">
                    No leads yet. Run a scout above to populate the pipeline.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* SIDE SHEET */}
      <AnimatePresence>
        {selectedLead && (
          <SideSheet lead={selectedLead} onClose={() => setSelectedLead(null)} onPitch={() => { setPitchLead(selectedLead); setSelectedLead(null); }} />
        )}
      </AnimatePresence>

      {/* PITCH STUDIO */}
      <AnimatePresence>
        {pitchLead && (
          <PitchStudio
            lead={pitchLead}
            services={meta?.services || []}
            tones={meta?.tones || []}
            onClose={() => setPitchLead(null)}
            onMarkSent={() => { updateStatus(pitchLead, "contacted"); setPitchLead(null); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ================= sub components ================= */

function Label({ children }) {
  return (
    <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1.5"
           style={{ fontFamily: "JetBrains Mono, monospace" }}>
      {children}
    </label>
  );
}

function Badge({ label, tone = "indigo", testid }) {
  const map = {
    indigo: "border-indigo-500/40 bg-indigo-500/10 text-indigo-300",
    emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
    amber: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  };
  return (
    <span data-testid={testid}
          className={`inline-flex items-center gap-1.5 border rounded-full px-3 py-1 text-[10px] uppercase tracking-widest ${map[tone]}`}
          style={{ fontFamily: "JetBrains Mono, monospace" }}>
      <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" /> {label}
    </span>
  );
}

function Kpi({ icon: Icon, label, value, tone, delay, testid }) {
  const glow = { indigo: "rgba(99,102,241,0.22)", emerald: "rgba(16,185,129,0.22)", amber: "rgba(245,158,11,0.22)", rose: "rgba(244,63,94,0.22)" }[tone];
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      className="relative overflow-hidden border border-slate-800/70 rounded p-4 bg-slate-950/40"
      data-testid={testid}
    >
      <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full pointer-events-none"
           style={{ background: `radial-gradient(closest-side, ${glow}, transparent 70%)` }} />
      <div className="relative flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-slate-400" style={{ fontFamily: "JetBrains Mono, monospace" }}>{label}</span>
        <Icon size={14} className="text-slate-500" />
      </div>
      <div className="relative mt-3 text-2xl lg:text-3xl font-bold text-white" style={{ fontFamily: "Outfit, Inter, sans-serif" }}>
        {value}
      </div>
    </motion.div>
  );
}

function SideSheet({ lead, onClose, onPitch }) {
  const pillars = lead.pillars || {};
  const pillarMeta = [
    { key: "web", label: "Web & App Engineering", color: "#6366F1" },
    { key: "ai", label: "AI Solutions & Automation", color: "#10B981" },
    { key: "growth", label: "Digital Growth & Media", color: "#F59E0B" },
    { key: "security", label: "Systems, Security & Infra", color: "#F43F5E" },
  ];
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-40 bg-slate-950/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.aside
        initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 260 }}
        onClick={(e) => e.stopPropagation()}
        className="absolute right-0 top-0 h-full w-full max-w-xl bg-[#0B0F17] border-l border-slate-800 overflow-y-auto"
        data-testid="scout-side-sheet"
      >
        <div className="sticky top-0 bg-[#0B0F17]/95 backdrop-blur border-b border-slate-800 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-indigo-300" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              // GAP ASSESSMENT
            </div>
            <div className="font-bold text-lg text-white mt-0.5" style={{ fontFamily: "Outfit, Inter, sans-serif" }}>
              {lead.name}
            </div>
          </div>
          <button onClick={onClose} data-testid="side-sheet-close" className="text-slate-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex items-center gap-4 flex-wrap text-xs text-slate-400">
            {lead.address && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {lead.address}</span>}
            {lead.phone && <span className="inline-flex items-center gap-1"><Phone size={12} /> {lead.phone}</span>}
            {lead.website && (
              <a href={lead.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200">
                <Globe size={12} /> visit site <ExternalLink size={10} />
              </a>
            )}
          </div>

          <div className="border border-indigo-500/30 bg-indigo-500/5 rounded p-4">
            <div className="text-[10px] uppercase tracking-widest text-indigo-300 mb-2" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              // RECOMMENDED PITCH ANGLE
            </div>
            <div className="text-white font-medium">{lead.recommended_service}</div>
            <div className="text-sm text-slate-300 mt-1">{lead.recommended_blurb}</div>
            <div className="text-xs text-slate-400 mt-3">Estimated project value: <span className="text-emerald-300 font-mono">{fmtCurrency(lead.estimated_value)}</span></div>
          </div>

          <div className="space-y-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-400" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              // 4-PILLAR GAP ASSESSMENT
            </div>
            {pillarMeta.map((p) => {
              const v = pillars[p.key] || { score: 0, reason: "—" };
              return (
                <div key={p.key} data-testid={`pillar-${p.key}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm text-slate-200">{p.label}</span>
                    <span className="text-xs font-mono" style={{ color: p.color, fontFamily: "JetBrains Mono, monospace" }}>{v.score}/100</span>
                  </div>
                  <div className="h-1.5 bg-slate-800 rounded overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }} animate={{ width: `${v.score}%` }} transition={{ duration: 0.6 }}
                      className="h-full rounded"
                      style={{ background: p.color, boxShadow: `0 0 12px ${p.color}66` }}
                    />
                  </div>
                  <div className="text-xs text-slate-400 mt-1.5">{v.reason}</div>
                </div>
              );
            })}
          </div>

          <button
            onClick={onPitch}
            data-testid="side-sheet-open-pitch"
            className="w-full inline-flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-400 text-white px-4 py-3 rounded font-mono text-xs uppercase tracking-widest transition-colors"
            style={{ fontFamily: "JetBrains Mono, monospace" }}
          >
            <Send size={14} /> Open Pitch Studio
          </button>
        </div>
      </motion.aside>
    </motion.div>
  );
}

function PitchStudio({ lead, services, tones, onClose, onMarkSent }) {
  const defaultService = lead.recommended_service || services[0]?.name || "";
  const [service, setService] = useState(defaultService);
  const [tone, setTone] = useState(tones[0] || "Consultative");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/scout/leads/${lead.id}/pitch`, { service, tone });
      setSubject(data.subject);
      setBody(data.body);
      toast.success("Pitch generated with Gemini 3.1 Pro");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Pitch generation failed");
    } finally {
      setBusy(false);
    }
  };

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`);
      toast.success("Copied to clipboard");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.aside
        initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 260 }}
        onClick={(e) => e.stopPropagation()}
        className="absolute right-0 top-0 h-full w-full max-w-2xl bg-[#0B0F17] border-l border-slate-800 overflow-y-auto"
        data-testid="pitch-studio"
      >
        <div className="sticky top-0 bg-[#0B0F17]/95 backdrop-blur border-b border-slate-800 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-indigo-300" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              // PITCH EMAIL STUDIO · GEMINI 3.1 PRO
            </div>
            <div className="font-bold text-lg text-white mt-0.5" style={{ fontFamily: "Outfit, Inter, sans-serif" }}>{lead.name}</div>
          </div>
          <button onClick={onClose} data-testid="pitch-close" className="text-slate-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Service</Label>
              <select value={service} onChange={(e) => setService(e.target.value)} data-testid="pitch-service"
                      className="w-full bg-slate-900/60 border border-slate-700 focus:border-indigo-500 outline-none rounded px-3 py-2.5 text-sm text-slate-100">
                {services.map((s) => (
                  <option key={s.key} value={s.name}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Tone</Label>
              <select value={tone} onChange={(e) => setTone(e.target.value)} data-testid="pitch-tone"
                      className="w-full bg-slate-900/60 border border-slate-700 focus:border-indigo-500 outline-none rounded px-3 py-2.5 text-sm text-slate-100">
                {tones.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={generate} disabled={busy}
              data-testid="pitch-generate"
              className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 text-white px-4 py-2.5 rounded font-mono text-xs uppercase tracking-widest transition-colors"
              style={{ fontFamily: "JetBrains Mono, monospace" }}
            >
              {busy ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {subject ? "Regenerate" : "Generate"}
            </button>
            {subject && (
              <>
                <button onClick={copyAll} data-testid="pitch-copy"
                        className="inline-flex items-center gap-2 border border-slate-700 hover:border-indigo-500 text-slate-200 px-4 py-2.5 rounded text-xs font-mono uppercase tracking-widest transition-colors"
                        style={{ fontFamily: "JetBrains Mono, monospace" }}>
                  <Copy size={14} /> Copy
                </button>
                <button onClick={onMarkSent} data-testid="pitch-mark-sent"
                        className="inline-flex items-center gap-2 border border-emerald-500/50 hover:bg-emerald-500/10 text-emerald-300 px-4 py-2.5 rounded text-xs font-mono uppercase tracking-widest transition-colors"
                        style={{ fontFamily: "JetBrains Mono, monospace" }}>
                  <CheckCircle2 size={14} /> Mark Sent
                </button>
              </>
            )}
          </div>

          <div>
            <Label>Subject</Label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Generate a pitch to populate..."
              data-testid="pitch-subject"
              className="w-full bg-slate-900/60 border border-slate-700 focus:border-indigo-500 outline-none rounded px-3 py-2.5 text-sm text-slate-100"
            />
          </div>
          <div>
            <Label>Body</Label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={14}
              placeholder="The generated cold email will appear here — fully editable before you send."
              data-testid="pitch-body"
              className="w-full bg-slate-900/60 border border-slate-700 focus:border-indigo-500 outline-none rounded px-3 py-2.5 text-sm text-slate-100 leading-relaxed font-body"
            />
          </div>
        </div>
      </motion.aside>
    </motion.div>
  );
}
